import {
  useAssetClassList,
  useAssetTypeList,
  useSearchAssetClassList,
  useToggleAssetClass,
  useToggleAssetType,
} from "@features/ax/settings/assetClassification/hooks";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { Collapsible, CollapsibleContent } from "@shared/ui/collapsible";
import { Input } from "@shared/ui/input";
import { Switch } from "@shared/ui/switch";
import { ChevronDown, ChevronUp, Edit, Plus, Settings } from "lucide-react";
import { useState } from "react";
import { AssetClass, DEFAULT_ASSET_CLASSIFICATIONS } from "../../../../types";
import { AssetTypeCreate } from "./assetTypeCreate";
import { AssetClassificationForm } from "./create";
import { FailureModeManagement } from "./failureModeManagement";
interface AssetClassItemProps {
  assetClass: any;
  onToggle: (classCode: string, isActive: boolean) => void;
  assetTypes: any[];
  onToggleAssetType: (
    classCode: string,
    typeCode: string,
    isActive: boolean
  ) => void;
  onManageFailureModes: (assetClass: any, assetType: any) => void;
  onToggleExpand: (assetClassCode: string, isOpen: boolean) => void;
  isExpanded: boolean;
}
const AssetClassItem: React.FC<AssetClassItemProps> = ({
  assetClass,
  onToggle,
  assetTypes = [],
  onToggleAssetType,
  onManageFailureModes,
  onToggleExpand,
  isExpanded,
}) => {
  const handleToggle = () => {
    onToggleExpand(assetClass.code, !isExpanded);
  };
  return (
    <div className="rounded-md border mb-3 transition-colors overflow-hidden">
      <div className="flex items-center justify-between p-4 hover:bg-muted/30">
        <div className="flex-1 cursor-pointer" onClick={handleToggle}>
          <div className="flex items-center gap-3">
            <span className="text-lg font-semibold text-blue-600">
              {assetClass.name}
            </span>
            <Badge variant="secondary">
              {assetClass.childCount || 0} types
            </Badge>
          </div>
          {assetClass.description && (
            <div className="mt-2">
              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md border-l-4 border-blue-200">
                <span className="font-medium text-gray-800">Description: </span>
                {assetClass.description}
              </p>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <AssetClassificationForm
            editData={assetClass}
            trigger={
              <Button variant="ghost" size="sm">
                <Edit className="w-4 h-4" />
              </Button>
            }
          />

          <Switch
            checked={assetClass.isActive}
            onCheckedChange={(value) => onToggle(assetClass.code, value)}
          />
          <Button variant="ghost" size="sm" onClick={handleToggle}>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
      <Collapsible open={isExpanded} onOpenChange={handleToggle}>
        <CollapsibleContent>
          <div className="bg-muted/20 p-4 border-t">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-sm font-medium">Asset Types</h4>
              <AssetTypeCreate
                trigger={
                  <Button className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Add Asset Type
                  </Button>
                }
                code={assetClass.code}
              />
            </div>
            {assetTypes.length > 0 ? (
              <div className="space-y-3">
                {assetTypes.map((assetType) => (
                  <div
                    key={assetType.code}
                    className="border rounded-lg p-4 bg-background"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex flex-col gap-2 flex-1">
                        <div className="flex items-center gap-3">
                          <h4 className="font-medium text-gray-900">
                            {assetType.name}
                          </h4>
                          <Badge variant="outline">
                            {assetType.failureModeCount || 0} failure modes
                          </Badge>
                        </div>
                        {assetType.description && (
                          <div className="w-full">
                            <p className="text-sm text-gray-600 bg-white p-3 rounded-md border-l-4 border-green-200">
                              <span className="font-medium text-gray-800">
                                Description:{" "}
                              </span>
                              {assetType.description}
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            onManageFailureModes(assetClass, assetType)
                          }
                          className="flex items-center gap-2"
                        >
                          <Settings className="w-4 h-4" />
                          Manage Failure Modes
                        </Button>
                        <AssetTypeCreate
                          editData={assetType}
                          trigger={
                            <Button variant="ghost" size="sm">
                              <Edit className="w-4 h-4" />
                            </Button>
                          }
                          code={assetType.code}
                        />
                        <Switch
                          checked={assetType.isActive}
                          onCheckedChange={(value) =>
                            onToggleAssetType(
                              assetClass.code,
                              assetType.code,
                              value
                            )
                          }
                        />
                      </div>
                    </div>
                    {/* Preview of Failure Modes */}
                    {assetType.childCount > 0 && (
                      <div className="text-sm text-gray-600 mt-2 pt-2 border-t border-gray-200">
                        <span className="font-medium">Failure Modes: </span>
                        {assetType.childCount} defined
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No asset types defined
              </p>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
export const AssetClassificationList = () => {
  const [assetClasses, setAssetClasses] = useState<AssetClass[]>(
    DEFAULT_ASSET_CLASSIFICATIONS
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAssetType, setSelectedAssetType] = useState<any>(null);
  const [selectedAssetClass, setSelectedAssetClass] = useState<any>(null);
  const [showFailureModes, setShowFailureModes] = useState(false);
  // SIMPLIFIED: Only track which single asset class is expanded
  const [expandedAssetClassCode, setExpandedAssetClassCode] = useState<
    string | null
  >(null);
  // Fetch asset classes
  const { data: assetClassList, isLoading: isLoadingAssetClasses } =
    useAssetClassList();
  const { data: assetSearchClassList, isLoading: isLoadingSearchAssetClasses } =
    useSearchAssetClassList(searchTerm);
  const { mutateAsync: toggleAssetClass, isPending: isClassUpdating } =
    useToggleAssetClass();
  const { mutateAsync: toggleAssetType, isPending: isTypeUpdating } =
    useToggleAssetType();
  // Conditionally fetch asset types only when an asset class is expanded
  const { data: assetTypeList, isLoading: isLoadingAssetTypes } =
    useAssetTypeList(expandedAssetClassCode || undefined);
  const getAssetTypesForClass = (assetClassCode: string): any[] => {
    if (expandedAssetClassCode === assetClassCode && assetTypeList) {
      const assetClass = assetClassList?.find(
        (ac) => ac.code === assetClassCode
      );
      const hierarchyCode = assetClass?.hierarchyCodes?.assetClass;
      if (!hierarchyCode) return [];
      return assetTypeList.filter(
        (assetType) => assetType.hierarchyCodes?.assetClass === hierarchyCode
      );
    }
    return [];
  };
  const handleAssetClassToggle = (assetClassCode: string, isOpen: boolean) => {
    if (isOpen) {
      setExpandedAssetClassCode(assetClassCode);
    } else {
      setExpandedAssetClassCode(null);
    }
  };

  const handleToggleAssetClassClick = (
    classCode: string,
    isActive: boolean
  ) => {
    toggleAssetClass({ code: classCode, isActive });
  };

  // Asset Type Delete Handlers
  const handleToggleAssetTypeClick = (
    classCode: string,
    typeCode: string,
    isActive: boolean
  ) => {
    toggleAssetType({ code: typeCode, isActive });
  };

  const handleManageFailureModes = (assetClass: any, assetType: any) => {
    setSelectedAssetType(assetType);
    setSelectedAssetClass(assetClass);
    setShowFailureModes(true);
  };
  if (isLoadingAssetClasses) {
    return (
      <div className="space-y-4">
        <div className="text-center py-6">
          <p className="text-sm text-muted-foreground">
            Loading asset classes...
          </p>
        </div>
      </div>
    );
  }
  return (
    <>
      {/* Action Buttons */}
      <div className="flex justify-between gap-4 mt-5">
        <div>
          <Input
            id="search"
            placeholder="Search asset classes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-96"
          />
        </div>
        <AssetClassificationForm
          trigger={
            <Button className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Add Asset Class
            </Button>
          }
        />
      </div>
      {/* Asset Classification Hierarchy */}
      <Card className="mt-5">
        <CardContent>
          {(searchTerm.length > 0 ? assetSearchClassList : assetClassList)
            ?.length > 0 ? (
            <div>
              {(searchTerm.length > 0
                ? assetSearchClassList
                : assetClassList
              ).map((assetClass) => (
                <AssetClassItem
                  key={assetClass.code}
                  assetClass={assetClass}
                  onToggle={handleToggleAssetClassClick}
                  assetTypes={getAssetTypesForClass(assetClass.code)}
                  onToggleAssetType={handleToggleAssetTypeClick}
                  onManageFailureModes={handleManageFailureModes}
                  onToggleExpand={handleAssetClassToggle}
                  isExpanded={expandedAssetClassCode === assetClass.code}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-6 border rounded-md bg-muted/30">
              <Settings className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
              <h3 className="font-medium mb-1">No Asset Classes</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
                No asset classes found matching "{searchTerm}". Try adjusting
                your search or create a new asset class.
              </p>
              <AssetClassificationForm
                trigger={
                  <Button>
                    <Plus className="h-4 w-4 mr-1" /> Add Asset Class
                  </Button>
                }
              />
            </div>
          )}
        </CardContent>
      </Card>

      <FailureModeManagement
        selectedAssetType={selectedAssetType}
        showFailureModes={showFailureModes}
        onClose={() => setShowFailureModes(false)}
      />
    </>
  );
};
